import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Diamond, 
  Users, 
  FileText, 
  Link2, 
  TrendingUp,
  Calendar,
  AlertCircle,
  CheckCircle,
  Gem,
  Eye
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { useSpinner } from '@/hooks/useSpinner';
import { multiNivelService, MultiNivelSummary } from '@/services/multi-nivel.service';
import DetalhesRede from './DetalhesRede';
import Ranking from '@/components/Ranking';
import AdesaoMultiNivel from '@/components/AdesaoMultiNivel';
import ExtratosMultiNivel from '@/components/ExtratosMultiNivel';

interface SummaryData extends MultiNivelSummary {}

const MinhaRede: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { isLoading, showSpinner, hideSpinner } = useSpinner();  

  const [isRankingOpen, setIsRankingOpen] = useState(false);

  const [summaryData, setSummaryData] = useState<SummaryData | null>(null);
  const [userData, setUserData] = useState<any>(null);
  const [isDetalhesRedeOpen, setIsDetalhesRedeOpen] = useState(false);
  const [hasValidAdesao, setHasValidAdesao] = useState<boolean | null>(null);
  const [checkingAdesao, setCheckingAdesao] = useState(true);

  useEffect(() => {
    checkAdesao();
  }, []);

  const checkAdesao = async () => {
    const userDataString = localStorage.getItem('userlogged');
    let parsedUserData;
    if (userDataString) {
      parsedUserData = JSON.parse(userDataString);
      setUserData(parsedUserData);
    }

    if (!parsedUserData?.id) {
      navigate('/home');
      return;
    }

    try {
      setCheckingAdesao(true);
      const adesaoResponse = await multiNivelService.getAdesaoByUserId(parsedUserData.id);
      
      if (adesaoResponse.success && adesaoResponse.data?.status === 'aderido') {
        // get codigo from .data
        const codigo = adesaoResponse.data.codigo;
        // atualizar codigo no userData
        parsedUserData.codigo = codigo;
        localStorage.setItem('userlogged', JSON.stringify(parsedUserData));        
        setUserData(parsedUserData);
        setHasValidAdesao(true);
        loadSummaryData(parsedUserData);
      } else {
        setHasValidAdesao(false);
      }
    } catch (error) {
      console.error('Erro ao verificar adesão:', error);
      setHasValidAdesao(false);
    } finally {
      setCheckingAdesao(false);
    }
  };

  const loadSummaryData = async (userDataParam?: any) => {
    const userDataToUse = userDataParam || userData;
    
    if (!userDataToUse?.id) return;

    try {
      showSpinner('Carregando', 'Buscando dados da rede...');

      const response = await multiNivelService.getSummaryByUserId(userDataToUse.id);
      setSummaryData(response);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      navigate('/home');
        
      toast({
        title: 'Aviso',
        description: 'Não conseguimos carregar seus dados de rede, tente novamente ou contate o suporte',
        variant: 'default'
      });
    } finally {
      hideSpinner();
    }
  };

  const handleAdesaoCompleta = () => {
    setHasValidAdesao(true);
    loadSummaryData();
  };

  const getDiamantesInteiros = (diamantes: number) => {
    return Math.floor(diamantes);
  };

  const getProgresso = (diamantes: number) => {
    const decimal = diamantes - Math.floor(diamantes);
    return decimal * 100;
  };

  const getProximoDiamante = (diamantes: number) => {
    return Math.floor(diamantes) + 1;
  };

  const formatMonth = (mesReferencia: string) => {
    const [year, month] = mesReferencia.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    const dateLocale =  date.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    return dateLocale.charAt(0).toUpperCase() + dateLocale.slice(1);
  };

  const getNextMonthFirstDay = () => {
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return nextMonth.toLocaleDateString('pt-BR');
  };

  if (checkingAdesao) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
        <div className="px-4 pt-6">
          <div className="animate-pulse space-y-4">
            <div className="h-32 bg-muted rounded-lg"></div>
            <div className="grid grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-20 bg-muted rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Se não tem adesão válida, mostrar tela de adesão
  if (hasValidAdesao === false) {
    return (
      <AdesaoMultiNivel 
        userData={userData} 
        onAdesaoCompleta={handleAdesaoCompleta}
      />
    );
  }

  if (!summaryData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
        <div className="px-4 pt-6">
          <div className="animate-pulse space-y-4">
            <div className="h-32 bg-muted rounded-lg"></div>
            <div className="grid grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-20 bg-muted rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const diamantesInteiros = getDiamantesInteiros(summaryData.total_diamantes_mes);
  const progresso = getProgresso(summaryData.total_diamantes_mes);
  const proximoDiamante = getProximoDiamante(summaryData.total_diamantes_mes);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background pb-12">
        <div className="px-4 pt-6">
            <div className="flex items-center justify-between">
                <div>
                <h1 className="text-2xl font-bold text-foreground">
                    Minha Rede
                </h1>
                <p className="text-muted-foreground text-sm">
                    Confira os dados da sua rede de indicações
                </p>
                </div>
                {/* <Badge variant="outline" className="text-primary border-primary bg-primary/5">
                    Revendedora
                </Badge> */}
            </div>
        </div>
      {/* Header com dados dos diamantes */}
      <div className="px-4 pt-6 pb-4">
        <Card className="bg-gradient-to-br from-purple/5 to-purple/10 border-purple/20">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-foreground">
              <Gem className="w-5 h-5" />
              {formatMonth(summaryData.mes_referencia)}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Progresso dos diamantes */}
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm gap-4">
                <span className="font-medium text-2xl">{diamantesInteiros}</span>
                <Progress 
                value={progresso} 
                className="h-3 bg-gray-400"
                />
                <span className="font-medium text-2xl">{proximoDiamante}</span>
              </div>
              
            </div>

            {/* Mensagem de diamantes ganhos */}
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                Você já acumulou{' '}
                <span className="font-bold ">
                  {diamantesInteiros} diamantes
                </span>
                {' '}com indicação
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Total acumulado: {summaryData.total_diamantes_mes.toFixed(2)} diamantes
              </p>
            </div>

            {/* Valor vendido/comprado */}
            <div className="bg-background/50 rounded-lg p-3 text-center">
              <p className="text-xs text-muted-foreground">
                {summaryData.role === 'revendedora' ? 'Vendas' : 'Compras'} no mês
              </p>
              <p className="text-lg font-bold text-foreground">
                R$ {summaryData.total_vendido_comprado_mes.toFixed(2)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Navegação rápida */}
      <div className="px-4 mb-6">
        <div className="grid grid-cols-4 gap-4">
          <button 
            onClick={() => setIsDetalhesRedeOpen(true)}
            className="flex flex-col items-center gap-2 p-3 rounded-xl bg-card hover:bg-accent transition-colors"
          >
            <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-primary" />
            </div>
            <span className="text-xs font-medium text-foreground">Ver Rede</span>
          </button>
          
          <button 
            onClick={() => navigate('/extrato-mensal')}
            className="flex flex-col items-center gap-2 p-3 rounded-xl bg-card hover:bg-accent transition-colors"
          >
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
            <span className="text-xs font-medium text-foreground">Extrato Mensal</span>
          </button>
          
          <button 
            onClick={() => navigate('/links-indicacao')}
            className="flex flex-col items-center gap-2 p-3 rounded-xl bg-card hover:bg-accent transition-colors"
          >
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <Link2 className="w-6 h-6 text-green-600" />
            </div>
            <span className="text-xs font-medium text-foreground">Links de Indicação</span>
          </button>
          
          <button 
            onClick={() => setIsRankingOpen(true)}
            className="flex flex-col items-center gap-2 p-3 rounded-xl bg-card hover:bg-accent transition-colors"
          >
            <div className="w-12 h-12 bg-purple/10 rounded-xl flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-purple" />
            </div>
            <span className="text-xs font-medium text-foreground">Ranking</span>
          </button>
        </div>
      </div>

      {/* Card dinâmico de status */}
      <div className="px-4 mb-6">
        {summaryData.atingiu_minimo ? (
          <Alert className="bg-green-50 border-green-200">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <AlertDescription className="text-green-800">
              <span className="font-medium">Diamantes disponíveis no dia {getNextMonthFirstDay()}</span>
              <br />
              <span className="text-xs">
                Seus diamantes da rede de indicação estarão disponíveis para usar na Loja de Recompensas em {getNextMonthFirstDay()}
              </span>
            </AlertDescription>
          </Alert>
        ) : (
          <Alert className="bg-yellow-50 border-yellow-200">
            <AlertCircle className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-yellow-800">
              <span className="font-medium">
                Você ainda não atingiu o mínimo de R${summaryData.valor_minimo_necessario.toFixed(2)} em{' '}
                {summaryData.role === 'revendedora' ? 'vendas' : 'compras'} no mês atual.
              </span>
              {/* <br />
              <span className="text-sm">
                Faltam R$ {(summaryData.valor_minimo_necessario - summaryData.total_vendido_comprado_mes).toFixed(2)} para ativar o repasse de diamantes.
              </span> */}
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Sessão de extratos */}
      <ExtratosMultiNivel usuarioId={userData?.id} />

      {/* Seção de informações adicionais */}
      <div className="px-4 space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <Calendar className="w-4 h-4" />
              Como funciona o repasse
            </CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground space-y-2">
            <p>
              • Os diamantes são acumulados durante todo o mês através das suas indicações
            </p>
            {summaryData.role == 'cliente' && (
            <p>
              • Suas compras também geram diamantes instantaneamente 
            </p>
            )}
            <p>
              • Para receber o repasse, você precisa atingir o valor mínimo em{' '}
              {summaryData.role === 'revendedora' ? 'vendas' : 'compras'}
            </p>
            <p>
              • O repasse é feito automaticamente no primeiro dia do mês seguinte
            </p>
            <p>
              • Os diamantes ficam disponíveis no saldo e podem ser trocados na Loja de Recompensas
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Espaçamento para navegação mobile */}
      <div className="h-20 lg:h-0"></div>

      {/* Bottom Sheet DetalhesRede */}
      <DetalhesRede 
        isOpen={isDetalhesRedeOpen} 
        onClose={() => setIsDetalhesRedeOpen(false)} 
      />
      
      {/* Modal de Ranking */}
      <Ranking 
        isOpen={isRankingOpen} 
        onClose={() => setIsRankingOpen(false)} 
      />
    </div>
  );
};

export default MinhaRede;
